import { useEffect, useRef } from 'react';
import { Encounter, Practitioner, CodeableConcept } from '@medplum/fhirtypes';
import dayjs from 'dayjs';
import { HL7 } from '@/consts/systems.consts';

export type ReasonMode = 'text' | 'icd10';

export interface EncounterFormState {
  encounterType: CodeableConcept | undefined;
  date: Date | null;
  time: string;
  attendingDoctor: Practitioner | undefined;
  additionalDoctors: Practitioner[];
  reasonMode: ReasonMode;
  reasonText: string;
  reasonCode: CodeableConcept | undefined;
  referringDoctors: Practitioner[];
  additionalNotes: string;
}

interface SettersState {
  setEncounterType: (value: CodeableConcept | undefined) => void;
  setDate: (value: Date | null) => void;
  setTime: (value: string) => void;
  setAttendingDoctor: (value: Practitioner | undefined) => void;
  setAdditionalDoctors: (value: Practitioner[]) => void;
  setReasonMode: (value: ReasonMode) => void;
  setReasonText: (value: string) => void;
  setReasonCode: (value: CodeableConcept | undefined) => void;
  setReferringDoctors: (value: Practitioner[]) => void;
  setAdditionalNotes: (value: string) => void;
}

interface UseEncounterFormInitializationParams {
  encounter: Encounter | undefined;
  currentState: EncounterFormState;
  setters: SettersState;
}

export const useEncounterFormInitialization = ({
  encounter,
  currentState,
  setters,
}: UseEncounterFormInitializationParams) => {
  const baselineRef = useRef<EncounterFormState | null>(null);

  // Initialize form from encounter when editing, then capture baseline
  useEffect(() => {
    const initialState: EncounterFormState = {
      encounterType: currentState.encounterType,
      date: currentState.date,
      time: currentState.time,
      attendingDoctor: currentState.attendingDoctor,
      additionalDoctors: currentState.additionalDoctors,
      reasonMode: currentState.reasonMode,
      reasonText: currentState.reasonText,
      reasonCode: currentState.reasonCode,
      referringDoctors: currentState.referringDoctors,
      additionalNotes: currentState.additionalNotes,
    };

    if (encounter) {
      // Set encounter type
      if (encounter.type?.[0]) {
        initialState.encounterType = encounter.type[0];
        setters.setEncounterType(encounter.type[0]);
      }

      // Set date and time
      if (encounter.period?.start) {
        const startDate = new Date(encounter.period.start);
        initialState.date = startDate;
        initialState.time = dayjs(startDate).format('HH:mm');
        setters.setDate(startDate);
        setters.setTime(dayjs(startDate).format('HH:mm'));
      }

      // Extract practitioners from participants
      const attendingParticipant = encounter.participant?.find(
        (p) =>
          p.type?.[0]?.coding?.[0]?.code ===
          HL7.TERMINOLOGY.CS.V3.PARTICIPATION_TYPE.CODES.ATND
      );
      if (attendingParticipant?.individual?.reference) {
        const practitionerId =
          attendingParticipant.individual.reference.replace(
            'Practitioner/',
            ''
          );
        const doctor = {
          resourceType: 'Practitioner',
          id: practitionerId,
          name: [{ text: attendingParticipant.individual.display || '' }],
        } as Practitioner;
        initialState.attendingDoctor = doctor;
        setters.setAttendingDoctor(doctor);
      }

      // Extract consultants (CON)
      const consultantParticipants =
        encounter.participant?.filter(
          (p) =>
            p.type?.[0]?.coding?.[0]?.code ===
            HL7.TERMINOLOGY.CS.V3.PARTICIPATION_TYPE.CODES.CON
        ) || [];
      const consultants = consultantParticipants
        .map((p) => {
          if (p.individual?.reference) {
            const id = p.individual.reference.replace('Practitioner/', '');
            return {
              resourceType: 'Practitioner',
              id,
              name: [{ text: p.individual.display || '' }],
            } as Practitioner;
          }
          return null;
        })
        .filter(Boolean) as Practitioner[];
      initialState.additionalDoctors = consultants;
      setters.setAdditionalDoctors(consultants);

      // Extract referring doctors (REF)
      const referringParticipants =
        encounter.participant?.filter(
          (p) =>
            p.type?.[0]?.coding?.[0]?.code ===
            HL7.TERMINOLOGY.CS.V3.PARTICIPATION_TYPE.CODES.REFERRER
        ) || [];
      const referring = referringParticipants
        .map((p) => {
          if (p.individual?.reference) {
            const id = p.individual.reference.replace('Practitioner/', '');
            return {
              resourceType: 'Practitioner',
              id,
              name: [{ text: p.individual.display || '' }],
            } as Practitioner;
          }
          return null;
        })
        .filter(Boolean) as Practitioner[];
      initialState.referringDoctors = referring;
      setters.setReferringDoctors(referring);

      // Set reason
      const reason = encounter.reasonCode?.[0];
      if (reason) {
        if (reason.text) {
          initialState.reasonMode = 'text';
          initialState.reasonText = reason.text;
          setters.setReasonMode('text');
          setters.setReasonText(reason.text);
        } else if (reason.coding?.[0]) {
          initialState.reasonMode = 'icd10';
          initialState.reasonCode = reason;
          setters.setReasonMode('icd10');
          setters.setReasonCode(reason);
        }
      }

      // Set additional notes from extension
      const noteExtension = encounter.extension?.find((ext) => ext.valueString);
      if (noteExtension?.valueString) {
        initialState.additionalNotes = noteExtension.valueString;
        setters.setAdditionalNotes(noteExtension.valueString);
      }
    }

    // Capture baseline directly from the initial state
    baselineRef.current = initialState;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [encounter?.id]);

  return {
    baselineRef,
  };
};
